import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { formatMoney } from "helpers/formatters"
import { planCardStyles, planButtonStyles } from "helpers/plan"
import {
  Card,
  Content,
  Title,
  Amount,
  Period,
  DescriptionList,
  DescriptionListItem,
  Actions,
  AdditionalLink,
  AdditionalTooltip,
  Subscribe,
} from "components/Subscription/Plan/elements"
import Toggle from "components/ui/InputToggle"
import { PlanOptionType, PlanType, PlanItemType } from "types"

type Props = {
  active: boolean
  disabled: boolean
  chosen?: string
  plan: PlanType
  selectedPeriod: string
  onToggle: (period: string) => void
  onSubscribe: (values: PlanOptionType) => void
}

type ItemType = {
  title: string
}

const PlanStandard = ({
  active,
  disabled,
  chosen,
  plan,
  selectedPeriod,
  onToggle,
  onSubscribe,
}: Props) => {
  const { t } = useTranslation()

  const { plan_type } = plan
  const { plans } = plan
  const items = plans.map(({ id, price, plan_period }: PlanItemType) => {
    return {
      id,
      price,
      label: t(`features.plan.period.${plan_period}.title`),
      value: plan_period,
    }
  })

  const [selectedPlan, selectPlan] = useState(items[0])

  useEffect(() => {
    const planItem = items.find(({ value }) => value === selectedPeriod)

    if (planItem) {
      selectPlan(planItem)
    }
  }, [selectedPeriod])

  const handleChange = (event: React.MouseEvent, newValue: string | null) => {
    if (newValue !== null) {
      const planItem = items.find(({ value }) => value === newValue)

      if (planItem) {
        selectPlan(planItem)
      }

      onToggle(newValue)
    }
  }

  const handleClick = () => {
    onSubscribe(selectedPlan)
  }

  const buttonText = () => {
    if (active) {
      return t("features.plan.current.title")
    } else if (disabled) {
      return t("features.plan.disabled.title")
    } else {
      return t("features.plan.subscribe.title")
    }
  }

  return (
    <Card
      elevation={0}
      data-testid="plan"
      sx={planCardStyles(active, disabled, plan, chosen)}
    >
      <Content>
        <Title variant="h4">{t(`features.plan.type.${plan_type}.title`)}</Title>

        {selectedPlan && (
          <Amount variant="h3">
            {formatMoney(
              selectedPlan.value === "month" ? "2.0" : selectedPlan.price,
            )}
          </Amount>
        )}

        {items && (
          <Period>
            <Toggle
              items={items}
              value={selectedPeriod}
              onChange={handleChange}
            />
          </Period>
        )}

        <DescriptionList>
          {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            t(`features.plan.type.${plan_type}.items`).map(
              (item: ItemType, index: number) => (
                <DescriptionListItem key={index}>
                  {item.title}
                </DescriptionListItem>
              ),
            )
          }
        </DescriptionList>
      </Content>

      <Actions>
        <AdditionalTooltip
          title={t("features.subscription.actions.more.title")}
          placement="top"
        >
          <AdditionalLink to="/pages/subscription" target="_blank">
            ...
          </AdditionalLink>
        </AdditionalTooltip>
        <Subscribe
          sx={planButtonStyles(active, disabled, plan, chosen)}
          disabled={active || disabled}
          onClick={handleClick}
        >
          {buttonText()}
        </Subscribe>
      </Actions>
    </Card>
  )
}

PlanStandard.defaultProps = {
  active: false,
  disabled: false,
}

export default PlanStandard
